import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/shared";
import { Megaphone, Send, Bell, Mail, Smartphone, Search, Loader2, Users, Check } from "lucide-react";

const AUDIENCES = [
  { key: "all", label: "All users", hint: "Every landlord & renter" },
  { key: "landlords", label: "All landlords", hint: "Landlord accounts only" },
  { key: "renters", label: "All renters", hint: "Renter accounts only" },
  { key: "selected", label: "Specific users", hint: "Pick individual people" },
];

export const AnnouncementsTab = ({ users = [] }) => {
  const [audience, setAudience] = useState("all");
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [chPush, setChPush] = useState(true);
  const [chEmail, setChEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/admin/broadcasts").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const nonAdmin = users.filter((u) => u.role !== "admin");
  const filtered = nonAdmin.filter((u) =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()));

  const toggleUser = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const recipientCount = audience === "selected" ? selected.length
    : audience === "landlords" ? nonAdmin.filter((u) => u.role === "landlord").length
    : audience === "renters" ? nonAdmin.filter((u) => u.role === "renter").length
    : nonAdmin.length;

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Add a title and message"); return; }
    if (audience === "selected" && selected.length === 0) { toast.error("Select at least one user"); return; }
    const channels = ["in_app", ...(chPush ? ["push"] : []), ...(chEmail ? ["email"] : [])];
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", {
        audience, user_ids: audience === "selected" ? selected : undefined,
        title: title.trim(), body: body.trim(), channels,
      });
      toast.success(`Announcement sent to ${data.recipient_count} recipient${data.recipient_count === 1 ? "" : "s"}`);
      setTitle(""); setBody(""); setSelected([]);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send announcement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 space-y-5">
        <div>
          <h2 className="text-xl font-heading font-medium text-ink flex items-center gap-2"><Megaphone className="h-5 w-5 text-forest" /> Send an announcement</h2>
          <p className="text-sm text-mute mt-1">Notify users about platform changes, billing/fee updates, outages or maintenance. Everyone always gets an in-app notification; add push or email too.</p>
        </div>

        <div className="bg-white border border-line rounded-xl p-5 space-y-5">
          <div>
            <Label>Audience</Label>
            <div className="mt-2 grid sm:grid-cols-2 gap-2">
              {AUDIENCES.map((a) => (
                <button
                  key={a.key} type="button" data-testid={`audience-${a.key}`}
                  onClick={() => setAudience(a.key)}
                  className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${audience === a.key ? "border-forest bg-forest/[0.04] ring-1 ring-forest/30" : "border-line hover:bg-bone"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-ink">{a.label}</span>
                    {audience === a.key && <Check className="h-4 w-4 text-forest" />}
                  </div>
                  <span className="text-xs text-mute">{a.hint}</span>
                </button>
              ))}
            </div>
          </div>

          {audience === "selected" && (
            <div data-testid="user-picker">
              <div className="flex items-center justify-between">
                <Label>Choose recipients ({selected.length} selected)</Label>
                {selected.length > 0 && <button type="button" onClick={() => setSelected([])} className="text-xs text-mute hover:text-ink">Clear</button>}
              </div>
              <div className="mt-1.5 relative">
                <Search className="h-4 w-4 text-mute absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email" className="pl-9 border-line" />
              </div>
              <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-line divide-y divide-line">
                {filtered.length === 0 ? (
                  <p className="text-sm text-mute p-3">No matching users.</p>
                ) : filtered.map((u) => (
                  <label key={u.id} data-testid={`pick-user-${u.id}`} className="flex items-center gap-3 px-3 py-2 hover:bg-bone cursor-pointer">
                    <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggleUser(u.id)} className="accent-forest h-4 w-4" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-ink truncate">{u.name} <span className="text-xs text-mute capitalize">· {u.role}</span></p>
                      <p className="text-xs text-mute truncate">{u.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label>Title</Label>
            <Input data-testid="broadcast-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Scheduled maintenance this Sunday" className="mt-1 border-line" maxLength={120} />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Write your announcement…" className="mt-1 border-line" />
          </div>

          <div>
            <Label>Delivery channels</Label>
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2 text-sm text-mute"><Bell className="h-4 w-4 text-forest" /> In-app notification <span className="text-xs">(always on)</span></div>
              <label className="flex items-center gap-2 text-sm text-ink cursor-pointer"><input data-testid="ch-push" type="checkbox" checked={chPush} onChange={(e) => setChPush(e.target.checked)} className="accent-forest h-4 w-4" /> <Smartphone className="h-4 w-4 text-forest" /> Push notification</label>
              <label className="flex items-center gap-2 text-sm text-ink cursor-pointer"><input data-testid="ch-email" type="checkbox" checked={chEmail} onChange={(e) => setChEmail(e.target.checked)} className="accent-forest h-4 w-4" /> <Mail className="h-4 w-4 text-forest" /> Email</label>
              <p className="text-xs text-mute">Push & email respect each user's notification preferences.</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <span data-testid="recipient-count" className="text-sm text-mute flex items-center gap-1.5"><Users className="h-4 w-4" /> {recipientCount} recipient{recipientCount === 1 ? "" : "s"}</span>
            <button data-testid="send-broadcast-btn" onClick={send} disabled={busy} className="bg-forest text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-forest-hover flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send announcement
            </button>
          </div>
        </div>
      </div>

      <div className="lg:col-span-2">
        <h3 className="font-heading font-medium text-ink mb-3">Recent announcements</h3>
        {history.length === 0 ? (
          <div className="bg-white border border-line rounded-xl"><EmptyState icon={Megaphone} title="Nothing sent yet" /></div>
        ) : (
          <div className="space-y-3">
            {history.map((b) => (
              <div key={b.id} data-testid={`broadcast-${b.id}`} className="bg-white border border-line rounded-xl p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-ink text-sm">{b.title}</p>
                  <span className="text-[11px] text-mute whitespace-nowrap">{new Date(b.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-xs text-mute mt-1 line-clamp-2">{b.body}</p>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                  <span className="rounded-full bg-forest/10 text-forest px-2 py-0.5">{b.audience_label}</span>
                  <span className="rounded-full bg-stone-100 text-stone-600 px-2 py-0.5">{b.recipient_count} recipients</span>
                  {(b.channels || []).map((c) => <span key={c} className="rounded-full border border-line px-2 py-0.5 text-mute">{c === "in_app" ? "in-app" : c}</span>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
